import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styles from "./choose-payment-plan.module.scss";
import { PaymentPlan, PurchaseTimelineType } from "../../../../model/leads/leads";
import { setPaymentPlan, setPaymentPlanDetails } from "../../../../stores/slices/get-solar-slice";
import { RootState } from "../../../../stores/store";
import { PaymentPlanOption } from "../get-solar.types";
import { InstalmentIcon, OneTimeIcon, PayAsYouUseIcon, ChevronRightIcon } from "./PaymentPlanIcons";
import { countryCurrencies, CountryCode } from "../../../../model/common/country";
import { currencyMap } from "../../../../model/common/money";
import { Button } from "../../components";

const paymentPlanOptions: PaymentPlanOption[] = [
	{
		id: PaymentPlan.Installment,
		label: "Installment",
		icon: <InstalmentIcon />,
	},
	{
		id: PaymentPlan.OneTime,
		label: "One-time",
		icon: <OneTimeIcon />,
	},
	{
		id: PaymentPlan.PayAsYouGo,
		label: "Pay as you use",
		icon: <PayAsYouUseIcon />,
	},
];

const timelineOptions = [
	{ id: PurchaseTimelineType.ReadyToBuyNow, label: "Ready to buy now" },
	{ id: PurchaseTimelineType.ReadyToBuyInTheNextThreeMonths, label: "In the next 3 months" },
	{ id: PurchaseTimelineType.NeedQuotationForAleterDate, label: "Need quotation for later" },
];

interface ChoosePaymentPlanProps {
	onCancel: () => void;
	onContinue: () => void;
}

export const ChoosePaymentPlan: React.FC<ChoosePaymentPlanProps> = ({ onCancel, onContinue }) => {
	const dispatch = useDispatch();
	const getSolar = useSelector((state: RootState) => state.getSolar);
	const countryCode = useSelector((state: RootState) => state.user.countryCode);
	const details = getSolar.paymentPlanDetails;

	const currencyCode = countryCurrencies[countryCode as CountryCode] ?? "NGN";
	const currencySymbol = currencyMap[currencyCode] ?? currencyCode;

	const [selectedPlan, setSelectedPlan] = useState<number | null>(getSolar.paymentPlan);
	const [alternatePowerExpense, setAlternatePowerExpense] = useState(details?.alternatePowerExpense ? String(details.alternatePowerExpense) : "");
	const [timelineType, setTimelineType] = useState<number | null>(details?.timelineType ?? null);
	const [solarMonthlyRepayment, setSolarMonthlyRepayment] = useState(details?.solarMonthlyRepayment ? String(details.solarMonthlyRepayment) : "");
	const [paymentPeriod, setPaymentPeriod] = useState(details?.paymentPeriod ? String(details.paymentPeriod) : "");
	const [oneTimePaymentAmount, setOneTimePaymentAmount] = useState(details?.oneTimePaymentAmount ? String(details.oneTimePaymentAmount) : "");

	const needsDetails = selectedPlan === PaymentPlan.Installment || selectedPlan === PaymentPlan.OneTime;
	const isInstallment = selectedPlan === PaymentPlan.Installment;
	const isOneTime = selectedPlan === PaymentPlan.OneTime;

	const isValid = () => {
		if (selectedPlan === null) return false;
		if (!needsDetails) return true;
		if (!alternatePowerExpense || Number(alternatePowerExpense) <= 0) return false;
		if (!timelineType) return false;
		if (isInstallment) {
			if (!solarMonthlyRepayment || Number(solarMonthlyRepayment) <= 0) return false;
			if (!paymentPeriod || Number(paymentPeriod) <= 0) return false;
		}
		if (isOneTime) {
			if (!oneTimePaymentAmount || Number(oneTimePaymentAmount) <= 0) return false;
		}
		return true;
	};

	const handleContinue = () => {
		if (!isValid() || selectedPlan === null) return;
		dispatch(setPaymentPlan(selectedPlan));

		if (needsDetails) {
			dispatch(
				setPaymentPlanDetails({
					alternatePowerExpense: Number(alternatePowerExpense),
					timelineType: timelineType!,
					solarMonthlyRepayment: isInstallment ? Number(solarMonthlyRepayment) : null,
					paymentPeriod: isInstallment ? Number(paymentPeriod) : null,
					oneTimePaymentAmount: isOneTime ? Number(oneTimePaymentAmount) : null,
				})
			);
		} else {
			dispatch(setPaymentPlanDetails(null));
		}

		onContinue();
	};

	return (
		<div className={styles.container}>
			<div className={styles.content}>
				<div className={styles.header}>
					<div className={styles.iconWrapper}>
						<span className={styles.questionMark}>❓</span>
					</div>
					<div className={styles.titleSection}>
						<h2 className={styles.title}>Choose A Payment Plan</h2>
						<p className={styles.subtitle}>How would you like to pay after recommendation?</p>
					</div>
				</div>

				<div className={styles.optionsList}>
					{paymentPlanOptions.map(option => (
						<div
							key={option.id}
							className={`${styles.optionItem} ${selectedPlan === option.id ? styles.optionItemSelected : ""}`}
							onClick={() => setSelectedPlan(option.id)}
						>
							<div className={styles.optionLeft}>
								<div className={styles.optionIcon}>{option.icon}</div>
								<span className={styles.optionLabel}>{option.label}</span>
							</div>
							<div className={styles.optionChevron}>
								<ChevronRightIcon />
							</div>
						</div>
					))}
				</div>

				{needsDetails && (
					<div className={styles.detailsSection}>
						<div className={styles.formField}>
							<label className={styles.fieldLabel}>How much do you spend on alternate power monthly? *</label>
							<div className={styles.inputWithSuffix}>
								<input
									type="number"
									className={styles.textInput}
									placeholder="E.g 5000"
									value={alternatePowerExpense}
									onChange={e => {
										const val = e.target.value;
										if (val === "" || Number(val) >= 0) setAlternatePowerExpense(val);
									}}
									min={0}
								/>
								<span className={styles.inputSuffix}>{currencySymbol}</span>
							</div>
						</div>

						<div className={styles.formField}>
							<label className={styles.fieldLabel}>When are you looking to purchase? *</label>
							<div className={styles.timelineOptions}>
								{timelineOptions.map(option => (
									<div
										key={option.id}
										className={`${styles.timelineOption} ${timelineType === option.id ? styles.timelineOptionSelected : ""}`}
										onClick={() => setTimelineType(option.id)}
									>
										{option.label}
									</div>
								))}
							</div>
						</div>

						{isInstallment && (
							<>
								<div className={styles.formField}>
									<label className={styles.fieldLabel}>How much can you pay monthly for solar? *</label>
									<div className={styles.inputWithSuffix}>
										<input
											type="number"
											className={styles.textInput}
											placeholder="E.g 10000"
											value={solarMonthlyRepayment}
											onChange={e => {
												const val = e.target.value;
												if (val === "" || Number(val) >= 0) setSolarMonthlyRepayment(val);
											}}
											min={0}
										/>
										<span className={styles.inputSuffix}>{currencySymbol}</span>
									</div>
								</div>
								<div className={styles.formField}>
									<label className={styles.fieldLabel}>Payment period (months) *</label>
									<div className={styles.inputWithSuffix}>
										<input
											type="number"
											className={styles.textInput}
											placeholder="E.g 12"
											value={paymentPeriod}
											onChange={e => {
												const val = e.target.value;
												if (val === "" || Number(val) >= 0) setPaymentPeriod(val);
											}}
											min={0}
										/>
										<span className={styles.inputSuffix}>Months</span>
									</div>
								</div>
							</>
						)}

						{isOneTime && (
							<div className={styles.formField}>
								<label className={styles.fieldLabel}>One-time payment amount *</label>
								<div className={styles.inputWithSuffix}>
									<input
										type="number"
										className={styles.textInput}
										placeholder="E.g 50000"
										value={oneTimePaymentAmount}
										onChange={e => {
											const val = e.target.value;
											if (val === "" || Number(val) >= 0) setOneTimePaymentAmount(val);
										}}
										min={0}
									/>
									<span className={styles.inputSuffix}>{currencySymbol}</span>
								</div>
							</div>
						)}
					</div>
				)}

				<div className={styles.footer}>
					<div className={styles.footerInner}>
						<Button onClick={onCancel} variant="secondary">
							Cancel
						</Button>
						<Button onClick={handleContinue} disabled={!isValid()} variant="primary">
							Continue
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
};
